import React, { useState, useCallback, useMemo, useEffect } from "react";
import { API_BASE } from "../utils/api";
import { 
  X, Maximize2, Minimize2, 
  Search, Briefcase, Calendar, 
  FileSignature, Mail, Activity, 
  ShieldCheck, FileText, Loader2,
  Bot, ChevronUp, ChevronDown
} from "lucide-react";

interface ManualWorkflowAppProps {
  isOpen: boolean;
  onClose: () => void;
  onInvokeTool: (tool: string, params: any) => Promise<any>;
}

// -------------------------------------------------------------
// Sub-components for each tool view
// -------------------------------------------------------------

const DEMO_EMAIL = "sunil.sks@gmail.com";

const generateFutureSlots = () => {
  const slots = [];
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  let current = new Date();
  current.setDate(current.getDate() + 1); // Start from tomorrow
  
  while (slots.length < 6) {
    // Basic weekday check (Sat=6, Sun=0)
    if (current.getDay() !== 0 && current.getDay() !== 6) { 
      const dayName = days[current.getDay()];
      const monthName = months[current.getMonth()];
      const dateNum = current.getDate();
      const dayStr = `${dayName} ${monthName} ${dateNum}`;
      
      const times = ["10:00 AM IST", "2:30 PM IST", "4:00 PM IST"];
      const morningSlot = { day: dayStr, time: "10:00 AM IST" };
      const afternoonSlot = { day: dayStr, time: "3:00 PM IST" };
      
      slots.push(morningSlot);
      slots.push(afternoonSlot);
    }
    current.setDate(current.getDate() + 1);
  }
  return slots.slice(0, 6);
};

const ResumeSearchView = React.memo(({ onSubmit, isLoading, results }: any) => {
  const [role, setRole] = useState("Software Engineer");
  const [expYears, setExpYears] = useState(0); // 0 means no filter
  const [location, setLocation] = useState("Any");
  const [skills, setSkills] = useState("");

  const minExpDisplay = expYears === 0 ? "0 experience" : `${expYears}+ years`;
  const incrementExp = () => setExpYears(prev => Math.min(prev + 1, 30));
  const decrementExp = () => setExpYears(prev => Math.max(prev - 1, 0));

  // Parse raw_response text into candidate cards
  const parsedCandidates = useMemo(() => {
    if (!results) return null;
    // If already structured JSON with results/candidates/raw_results array
    if (Array.isArray(results?.results) && results.results.length > 0) {
      return results.results;
    }
    if (Array.isArray(results?.candidates) && results.candidates.length > 0) {
      return results.candidates;
    }
    if (Array.isArray(results?.raw_results) && results.raw_results.length > 0) {
      return results.raw_results;
    }
    // Try parsing from raw_response text (LLM conversational output)
    const rawText = results?.raw_response || results?.text || '';
    if (!rawText) return null;

    // Try to extract candidate info using pattern matching
    // Pattern: **Name:** X years of experience, skilled in Y
    const candidateRegex = /\*\*([^*]+?)\*\*[:\s]*([\d.]+)\s*(?:years?|yrs?)\s*(?:of\s*)?experience[^\n]*/gi;
    const matches = [...rawText.matchAll(candidateRegex)];
    if (matches.length > 0) {
      return matches.map((m: RegExpMatchArray) => {
        const name = m[1].trim().replace(/:$/, '');
        const exp = parseFloat(m[2]);
        // Extract skills after "skilled in" or "skills in" or "with skills"
        const fullLine = m[0];
        const skillsMatch = fullLine.match(/(?:skilled in|skills?(?:\s+in)?|proficient in|with skills?)\s+([^.\n]+)/i);
        const extractedSkills = skillsMatch ? skillsMatch[1].trim() : '';
        return {
          candidate_name: name,
          experience_years: exp,
          skills: extractedSkills,
          current_role: '',
          score: null
        };
      });
    }
    return null; // Could not parse
  }, [results]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar">
        <form id="resume_search_form" onSubmit={(e) => {
          e.preventDefault();
          // Build a natural-language query string that the backend SQL parser understands.
          // This matches the format produced by match_resumes.py so _parse_natural_language_query
          // can pick up role, experience, location and skills via its regex patterns.
          const queryParts: string[] = [];
          if (role && role.trim()) queryParts.push(`role: ${role.trim()}`);
          if (expYears > 0) queryParts.push(`${expYears} years experience`);
          if (location && location.trim() && location.toLowerCase() !== "any") queryParts.push(`location: ${location.trim()}`);
          if (skills && skills.trim()) queryParts.push(`skills: ${skills.trim()}`);
          const builtQuery = queryParts.join(" ") || "All candidates";
          onSubmit("match_resumes", { role, minExp: String(expYears), location, skills, query: builtQuery });
        }} className="space-y-4">
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Role / JD keywords</label>
            <input type="text" value={role} onChange={e => setRole(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Min experience</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={minExpDisplay}
                  readOnly
                  className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 pr-8 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors cursor-default"
                />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col">
                  <button
                    type="button"
                    onClick={incrementExp}
                    className="flex items-center justify-center w-[22px] h-[14px] rounded-t-[4px] bg-gray-200 dark:bg-[#2c3650] hover:bg-blue-100 dark:hover:bg-[#4f7cff]/25 text-gray-600 dark:text-[#7e8ba0] hover:text-blue-600 dark:hover:text-[#7da8ff] transition-colors"
                  >
                    <ChevronUp className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={decrementExp}
                    className="flex items-center justify-center w-[22px] h-[14px] rounded-b-[4px] bg-gray-200 dark:bg-[#2c3650] hover:bg-blue-100 dark:hover:bg-[#4f7cff]/25 text-gray-600 dark:text-[#7e8ba0] hover:text-blue-600 dark:hover:text-[#7da8ff] transition-colors"
                  >
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Location</label>
              <input type="text" list="locationList" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Any, Remote" className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
              <datalist id="locationList">
                <option value="Any" />
                <option value="Doha" />
                <option value="Remote" />
              </datalist>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Required skills</label>
            <input type="text" value={skills} onChange={e => setSkills(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors mb-2" />
            <div className="flex flex-wrap gap-1.5">
              {skills.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => (
                <span key={i} className="px-2.5 py-0.5 rounded-full bg-blue-50 dark:bg-[#4f7cff]/15 border border-blue-200 dark:border-[#4f7cff]/25 text-blue-600 dark:text-[#7da8ff] text-[10px]">{s}</span>
              ))}
            </div>
            <div className="pt-3 flex justify-end">
              <button
                 type="button"
                 onClick={() => {
                   const queryParts: string[] = [];
                   if (role && role.trim()) queryParts.push(role.trim());
                   if (skills && skills.trim()) queryParts.push(skills.trim());
                   const builtQuery = queryParts.join(" ");
                   onSubmit("search_linkedin_people", { keywords: builtQuery || "Technology", location: location !== "Any" ? location : undefined });
                 }}
                 className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-md bg-[#0a66c2]/10 text-[#0a66c2] hover:bg-[#0a66c2]/20 transition-colors border border-[#0a66c2]/20"
              >
                <Search className="w-3 h-3" />
                Search on LinkedIn
              </button>
            </div>
          </div>
        </form>

        {/* Results Area */}
        <div className="mt-6">
          <div className="text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-3">Results</div>
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-[#7e8ba0]">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <p className="text-[12px]">Searching candidates...</p>
            </div>
          )}
          {!isLoading && results && (
            <div className="space-y-2">
              {parsedCandidates && parsedCandidates.length > 0 ? (
                <>
                  <div className="text-[11px] text-gray-500 dark:text-[#7e8ba0] mb-2 flex items-center justify-between">
                    <span>Found {parsedCandidates.length} candidate(s)</span>
                    {results?.linkedin_url && (
                      <a href={results.linkedin_url} target="_blank" rel="noreferrer" className="text-[10px] text-[#0a66c2] hover:underline flex items-center gap-1">
                        View all on LinkedIn →
                      </a>
                    )}
                  </div>
                  {parsedCandidates.map((c: any, i: number) => {
                    const isLinkedIn = c.source === 'LinkedIn';
                    return (
                    <div key={i} className={`bg-gray-50 dark:bg-[#252e42] border rounded-lg p-3 hover:border-blue-500 dark:hover:border-white/20 transition-colors ${isLinkedIn ? 'border-[#0a66c2]/30 dark:border-[#0a66c2]/20' : 'border-gray-200 dark:border-white/10'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-9 h-9 rounded-md flex items-center justify-center text-[11px] font-bold shrink-0 ${isLinkedIn ? 'bg-gradient-to-br from-[#0a66c2]/20 to-[#004182]/20 text-[#0a66c2]' : 'bg-gradient-to-br from-blue-500/20 to-indigo-500/20 dark:from-blue-500/30 dark:to-indigo-500/30 text-blue-700 dark:text-blue-300'}`}>
                          {isLinkedIn ? 'in' : (c.candidate_name || c.name || 'U').substring(0,2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-gray-900 dark:text-[#dde3f0] flex items-center gap-1.5">
                            {c.candidate_name || c.name || "Unknown"}
                            {isLinkedIn && <span className="text-[8px] px-1.5 py-0.5 rounded bg-[#0a66c2]/10 text-[#0a66c2] font-medium uppercase tracking-wider">LinkedIn</span>}
                          </div>
                          <div className="text-[10px] text-gray-500 dark:text-[#7e8ba0]">
                            {c.job_title || c.current_role || c.role || "Candidate"}
                            {isLinkedIn && c.location ? ` · ${c.location}` : !isLinkedIn ? ` · ${c.experience_years ?? 0} yrs exp` : ''}
                          </div>
                        </div>
                        {(c.match_score || c.score) != null && Number(c.match_score || c.score) > 0 && (
                          <div className="ml-auto px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/25 text-green-600 dark:text-green-500 shrink-0">
                            {Math.round(Number(c.match_score || c.score))}%
                          </div>
                        )}
                      </div>
                      {/* Summary */}
                      {(c.summary || c.education) && (
                        <div className={`text-[11px] text-gray-600 dark:text-[#8896b0] mb-2 leading-relaxed whitespace-pre-line ${isLinkedIn ? 'line-clamp-4' : 'line-clamp-2'}`}>
                          {c.summary || c.education}
                        </div>
                      )}
                      {/* Skills */}
                      {c.skills && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {(typeof c.skills === 'string' ? c.skills.split(',').map((s: string) => s.trim()) : (c.matched_skills || c.skills || [])).filter(Boolean).slice(0, 6).map((skill: string, sIdx: number) => (
                            <span key={sIdx} className="px-2 py-0.5 rounded text-[10px] bg-blue-50 dark:bg-[#2c3650] text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-500/20">
                              {skill}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Actions row */}
                      <div className="flex gap-2 mt-1">
                        {/* Prefer download_url (7-day signed GCS URL generated by match_resumes).
                            Only fall back to a direct API endpoint if no signed URL is available.
                            Never use raw file_path (gs://...) — browsers can't access those. */}
                        {(c.download_url || c.file_name) && (
                          <a
                            href={
                              c.download_url ||
                              `${API_BASE}/api/resumes/${encodeURIComponent(c.file_name || '')}`
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] px-2.5 py-1 rounded-md bg-blue-600 dark:bg-[#4f7cff] text-white hover:bg-blue-700 dark:hover:bg-[#3d63d4] transition-colors inline-flex items-center gap-1"
                          >
                            {c.download_url?.includes('linkedin.com') ? '🌐 Open LinkedIn' : '📄 Download Resume'}
                          </a>
                        )}
                        {/* LinkedIn-sourced: Open on LinkedIn button */}
                        {isLinkedIn && (
                          <a
                            href={`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(c.candidate_name || '')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] px-2.5 py-1 rounded-md bg-[#0a66c2] text-white hover:bg-[#004182] transition-colors inline-flex items-center gap-1"
                          >
                            🔗 Open on LinkedIn
                          </a>
                        )}
                        {/* Local DB: Check LinkedIn button */}
                        {!isLinkedIn && (c.candidate_name || c.name) && (
                          <button
                            type="button"
                            onClick={() => onSubmit("search_linkedin_people", { keywords: c.candidate_name || c.name })}
                            className="text-[10px] px-2.5 py-1 rounded-md bg-[#0a66c2]/10 text-[#0a66c2] hover:bg-[#0a66c2]/20 transition-colors inline-flex items-center gap-1 border border-[#0a66c2]/20"
                          >
                            🌐 Check LinkedIn
                          </button>
                        )}
                      </div>
                    </div>
                  );})}
                </>
              ) : (
                <div className="bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-lg p-4 text-[13px] text-gray-700 dark:text-[#c4ccdd] leading-relaxed whitespace-pre-wrap">
                  {results.raw_response || results.message || results.answer || results.error || results.text || "No candidates found matching the criteria."}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const JobPostingView = React.memo(({ onSubmit, isLoading, results }: any) => {
  const [title, setTitle] = useState("Senior Frontend Engineer");
  const [location, setLocation] = useState("Doha / Remote");
  const [band, setBand] = useState("Band 7 · QAR 28k–35k");
  const [mustHave, setMustHave] = useState("React, TypeScript, Testing");
  const [niceToHave, setNiceToHave] = useState("Next.js, GraphQL, UX writing");
  const [publishTo, setPublishTo] = useState("LinkedIn + Company Portal");
  const [notes, setNotes] = useState("");

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar">
        <form id="job_posting_form" onSubmit={(e) => {
          e.preventDefault();
          onSubmit("generate_job_posting", { title, location, band, mustHave, niceToHave, publishTo, notes });
        }} className="space-y-4">
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Role title</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Location</label>
              <input type="text" value={location} onChange={e => setLocation(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Band & budget</label>
              <input type="text" value={band} onChange={e => setBand(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Must-have skills</label>
            <input type="text" value={mustHave} onChange={e => setMustHave(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors mb-2" />
            <div className="flex flex-wrap gap-1.5">
              {mustHave.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => (
                <span key={i} className="px-2.5 py-0.5 rounded-full bg-blue-50 dark:bg-[#4f7cff]/15 border border-blue-200 dark:border-[#4f7cff]/25 text-blue-600 dark:text-[#7da8ff] text-[10px]">{s}</span>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Nice to have</label>
            <input type="text" value={niceToHave} onChange={e => setNiceToHave(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Publish to</label>
            <select value={publishTo} onChange={e => setPublishTo(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors">
              <option>LinkedIn + Company Portal</option>
              <option>LinkedIn only</option>
              <option>Internal only</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Additional notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Specifics..." className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors min-h-[70px] resize-y" />
          </div>
        </form>
        {results && <SuccessBox results={results} />}
      </div>
    </div>
  );
});

const ScheduleInterviewView = React.memo(({ onSubmit, isLoading, results }: any) => {
  const [candidateName, setCandidateName] = useState("Sunil Agarwal");
  const [candidateEmail, setCandidateEmail] = useState(DEMO_EMAIL);
  const [interviewerName, setInterviewerName] = useState("Priya (HR), Rahul (Tech Lead)");
  const [interviewerEmail, setInterviewerEmail] = useState("priya@saturniq.tech");
  const [roundType, setRoundType] = useState("Technical Round");
  const [platform, setPlatform] = useState("Google Meet");

  const slots = useMemo(() => generateFutureSlots(), []);
  const [selectedSlot, setSelectedSlot] = useState(`${slots[0].day} | ${slots[0].time}`);


  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar">
        <form id="schedule_interview_form" onSubmit={(e) => {
          e.preventDefault();
          onSubmit("schedule_interview", { 
            candidate_name: candidateName, 
            candidate_email: candidateEmail,
            interviewer_name: interviewerName,
            interviewer_email: interviewerEmail,
            roundType, 
            platform, 
            slot: selectedSlot 
          });
        }} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Candidate Name</label>
              <input type="text" value={candidateName} onChange={e => setCandidateName(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Candidate Email</label>
              <input type="email" value={candidateEmail} onChange={e => setCandidateEmail(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Interviewers</label>
              <input type="text" value={interviewerName} onChange={e => setInterviewerName(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Interviewer Emails</label>
              <input type="text" value={interviewerEmail} onChange={e => setInterviewerEmail(e.target.value)} placeholder="comma separated for multiple" className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Round type</label>
            <select value={roundType} onChange={e => setRoundType(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors">
              <option>Technical Round</option>
              <option>HR Screening</option>
              <option>System Design</option>
              <option>Final Round</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-2">Available slots — select one</label>
            <div className="grid grid-cols-3 gap-2">
              {slots.map((s, i) => {
                const isSelected = selectedSlot === `${s.day} | ${s.time}`;
                return (
                  <div key={i} onClick={() => setSelectedSlot(`${s.day} | ${s.time}`)}
                    className={`p-2 rounded-md text-center cursor-pointer border transition-colors ${isSelected ? 'bg-blue-50 dark:bg-[#4f7cff]/15 border-blue-500 dark:border-[#4f7cff]' : 'bg-gray-50 dark:bg-[#252e42] border-gray-200 dark:border-white/10 hover:border-blue-400'}`}>
                    <div className={`font-medium text-[11px] mb-0.5 ${isSelected ? 'text-blue-700 dark:text-[#dde3f0]' : 'text-gray-900 dark:text-[#dde3f0]'}`}>{s.day}</div>
                    <div className={`text-[10px] ${isSelected ? 'text-blue-600 dark:text-[#7da8ff]' : 'text-gray-500 dark:text-[#7e8ba0]'}`}>{s.time}</div>
                  </div>
                )
              })}
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Platform</label>
            <select value={platform} onChange={e => setPlatform(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors">
              <option>Google Meet</option>
              <option>Zoom</option>
              <option>MS Teams</option>
              <option>In-person</option>
            </select>
          </div>
        </form>
        {results && <SuccessBox results={results} />}
      </div>
    </div>
  );
});

const OfferLetterView = React.memo(({ onSubmit, isLoading, results }: any) => {
  const [candidate, setCandidate] = useState("Sunil Agarwal");
  const [candidateEmail, setCandidateEmail] = useState(DEMO_EMAIL);
  const [role, setRole] = useState("Senior Frontend Engineer");
  const [joiningDate, setJoiningDate] = useState("2025-04-15");
  const [ctc, setCtc] = useState("QAR 30,000/month");
  const [template, setTemplate] = useState("Standard Offer Letter");
  const [clauses, setClauses] = useState("6-month probation period. Relocation support provided.");
  const [deliverVia, setDeliverVia] = useState("Email to candidate");
  
  const [history, setHistory] = useState<any[]>([]);
  
  const fetchHistory = async () => {
    try {
      const baseUrl = API_BASE;
      const res = await fetch(`${baseUrl}/api/offer-letters`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (err) {
      console.error("Failed to fetch offer letter history", err);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [results]); // Refresh when new results come in

  const handleDelete = async (filename: string) => {
    try {
      const baseUrl = API_BASE;
      const res = await fetch(`${baseUrl}/api/offer-letters/${filename}`, { method: 'DELETE' });
      if (res.ok) {
        fetchHistory();
      }
    } catch (err) {
      console.error("Failed to delete", err);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar">
        <form id="offer_letter_form" onSubmit={(e) => {
          e.preventDefault();
          onSubmit("manage_offer_letter_from_template", { 
            mode: "draft",
            candidate_name: candidate,
            recipient_email: candidateEmail,
            designation: role, 
            joining_date: joiningDate, 
            ctc: ctc, 
            basic: "50%",
            hra: "20%",
            conveyance: "10%",
            special: "15%",
            bonus: "5%",
            template, 
            clauses, 
            deliverVia 
          });
        }} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Candidate name</label>
              <input type="text" value={candidate} onChange={e => setCandidate(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Candidate Email</label>
              <input type="email" value={candidateEmail} onChange={e => setCandidateEmail(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Role</label>
            <input type="text" value={role} onChange={e => setRole(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Joining date</label>
              <input type="date" value={joiningDate} onChange={e => setJoiningDate(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">CTC</label>
              <input type="text" value={ctc} onChange={e => setCtc(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Template</label>
            <select value={template} onChange={e => setTemplate(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors">
              <option>Standard Offer Letter</option>
              <option>Executive Offer</option>
              <option>Contract / Consulting</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Additional clauses</label>
            <textarea value={clauses} onChange={e => setClauses(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors min-h-[70px] resize-y" />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Deliver via</label>
            <select value={deliverVia} onChange={e => setDeliverVia(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors">
              <option>Email to candidate</option>
              <option>Download PDF only</option>
              <option>Both</option>
            </select>
          </div>
        </form>
        {results && <SuccessBox results={results} />}

        {history.length > 0 && (
          <div className="mt-8">
            <h3 className="text-[12px] font-semibold text-gray-700 dark:text-[#dde3f0] mb-3 uppercase tracking-wider">Generated History</h3>
            <div className="space-y-2">
              {history.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-lg">
                  <div className="flex flex-col overflow-hidden">
                    <a href={item.url} target="_blank" rel="noreferrer" className="text-[12px] font-medium text-blue-600 dark:text-blue-400 hover:underline truncate">
                      {item.name}
                    </a>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                      {new Date(item.created_at * 1000).toLocaleString()} • {(item.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                  <button onClick={() => handleDelete(item.name)} className="ml-3 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition-colors" title="Delete">
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

const SendEmailView = React.memo(({ onSubmit, isLoading, results }: any) => {
  const [to, setTo] = useState(`Sunil Agarwal <${DEMO_EMAIL}>`);
  const [emailType, setEmailType] = useState("interview");
  const [subject, setSubject] = useState("Interview Invitation — Senior Frontend Engineer");
  const [message, setMessage] = useState("Dear Sunil,\n\nWe are pleased to invite you for a technical interview.\n\nDate: Thursday, March 27\nTime: 3:00 PM IST | Google Meet\n\nPlease confirm your availability.\n\nBest regards,\nRecruitment Team");

  const handleTypeChange = (type: string) => {
    setEmailType(type);
    if(type === 'interview') {
      setSubject("Interview Invitation — Senior Frontend Engineer");
      setMessage("Dear Sunil,\n\nWe are pleased to invite you for a technical interview.\n\nDate: Thursday, March 27\nTime: 3:00 PM IST | Google Meet\n\nPlease confirm your availability.\n\nBest regards,\nRecruitment Team");
    } else if(type === 'shortlist') {
      setSubject("Shortlisted — Senior Frontend Engineer");
      setMessage("Dear Sunil,\n\nCongratulations! You have been shortlisted. We will contact you shortly for next steps.\n\nBest regards,\nRecruitment Team");
    } else if(type === 'reject') {
      setSubject("Application Update — Senior Frontend Engineer");
      setMessage("Dear Sunil,\n\nThank you for your interest. Unfortunately, we will not be moving forward at this time.\n\nBest regards,\nRecruitment Team");
    } else if(type === 'offer') {
      setSubject("Offer Letter — Senior Frontend Engineer");
      setMessage("Dear Sunil,\n\nWe are delighted to extend an offer. Please find the offer letter attached.\n\nBest regards,\nRecruitment Team");
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar">
        <form id="send_email_form" onSubmit={(e) => {
          e.preventDefault();
          onSubmit("send_candidate_email", { to, emailType, subject, message });
        }} className="space-y-4">
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">To</label>
            <input type="text" value={to} onChange={e => setTo(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Email type</label>
            <select value={emailType} onChange={e => handleTypeChange(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors">
              <option value="interview">Interview Invite</option>
              <option value="shortlist">Shortlist Notification</option>
              <option value="reject">Rejection</option>
              <option value="offer">Offer Congratulations</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Subject</label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors" />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Message</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors min-h-[110px] resize-y" />
          </div>
        </form>
        {results && <SuccessBox results={results} />}
      </div>
    </div>
  );
});

const AppStatusView = React.memo(({ onSubmit, isLoading, results, onInvokeTool }: any) => {
  const [allApplicants, setAllApplicants] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedApplicant, setSelectedApplicant] = useState<any>(null);
  const [newStatus, setNewStatus] = useState("");
  const [newStage, setNewStage] = useState("");
  const [isFetching, setIsFetching] = useState(false);

  const STAGES = [
    "Applied", "Screening", "Shortlisted", "Interview Scheduled", 
    "Technical Round", "Final Interview", "Offered", "Hired", 
    "Offer Declined", "Rejected"
  ];

  const STATUSES = ["active", "hold", "rejected", "closed"];

  const fetchApplicants = useCallback(async () => {
    setIsFetching(true);
    try {
      const resp = await onInvokeTool("get_application_status", { mode: "list" });
      if (resp?.status === "success" && Array.isArray(resp.applications)) {
        setAllApplicants(resp.applications);
      }
    } catch (err) {
      console.error("Failed to fetch applicants:", err);
    } finally {
      setIsFetching(false);
    }
  }, [onInvokeTool]);

  useEffect(() => {
    fetchApplicants();
  }, [fetchApplicants]);

  const filteredApplicants = useMemo(() => {
    if (!searchQuery) return allApplicants;
    const q = searchQuery.toLowerCase();
    return allApplicants.filter(a => 
      (a.candidate_name || "").toLowerCase().includes(q) ||
      (a.company || "").toLowerCase().includes(q) ||
      (a.role || "").toLowerCase().includes(q) ||
      (a.status || "").toLowerCase().includes(q) ||
      (a.stage || "").toLowerCase().includes(q)
    );
  }, [allApplicants, searchQuery]);

  const handleSelectApplicant = (applicant: any) => {
    setSelectedApplicant(applicant);
    setNewStatus(applicant.status || "");
    setNewStage(applicant.stage || "");
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedApplicant) return;

    await onSubmit("get_application_status", {
      mode: "update",
      candidate_name: selectedApplicant.candidate_name,
      company_name: selectedApplicant.company,
      status: newStatus,
      stage: newStage
    });
    
    // Refresh the list to show updated status
    setTimeout(fetchApplicants, 1000);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-hidden flex flex-col px-5 py-4">
        <div className="mb-4">
          <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1.5">Search Applicants</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              type="text" 
              placeholder="Type name, role, or company..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md pl-9 pr-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        <div className="flex-1 flex gap-4 min-h-0 overflow-hidden">
          {/* List Section */}
          <div className="w-1/2 flex flex-col min-h-0">
            <div className="text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-2 flex justify-between">
              <span>Results ({filteredApplicants.length})</span>
              {isFetching && <Loader2 className="w-3 h-3 animate-spin" />}
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2 pr-1">
              {filteredApplicants.length > 0 ? (
                filteredApplicants.map((a, i) => (
                  <div 
                    key={i} 
                    onClick={() => handleSelectApplicant(a)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedApplicant?.candidate_name === a.candidate_name && selectedApplicant?.company === a.company
                        ? "bg-blue-50 dark:bg-[#4f7cff]/10 border-blue-500 dark:border-[#4f7cff]" 
                        : "bg-gray-50 dark:bg-[#252e42] border-gray-200 dark:border-white/5 hover:border-gray-300 dark:hover:border-white/20"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <div className="text-[13px] font-semibold text-gray-900 dark:text-[#dde3f0] truncate">{a.candidate_name}</div>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full uppercase font-bold ${
                        a.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' :
                        a.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400'
                      }`}>
                        {a.status}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-600 dark:text-[#8896b0] truncate">{a.role} @ {a.company}</div>
                    <div className="mt-2 flex items-center gap-2">
                       <span className="text-[10px] text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 px-1.5 py-0.5 rounded">{a.stage}</span>
                       <span className="text-[9px] text-gray-400 ml-auto">{a.last_updated?.split(' ')[0]}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-10 text-gray-500 dark:text-gray-600 text-[12px]">No applicants found</div>
              )}
            </div>
          </div>

          {/* Edit Section */}
          <div className="w-1/2 flex flex-col min-h-0 bg-gray-50 dark:bg-[#1d2535]/50 rounded-xl border border-gray-200 dark:border-white/5 p-4">
            {selectedApplicant ? (
              <form id="app_status_form" onSubmit={handleUpdate} className="space-y-4">
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200 dark:border-white/10">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold text-sm">
                    {selectedApplicant.candidate_name.substring(0,2).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-[14px] font-bold text-gray-900 dark:text-[#dde3f0]">{selectedApplicant.candidate_name}</div>
                    <div className="text-[11px] text-gray-500">{selectedApplicant.company}</div>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Application Stage</label>
                  <select 
                    value={newStage} 
                    onChange={e => setNewStage(e.target.value)}
                    className="w-full bg-white dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors"
                  >
                    {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Overall Status</label>
                  <select 
                    value={newStatus} 
                    onChange={e => setNewStatus(e.target.value)}
                    className="w-full bg-white dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors"
                  >
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="pt-2">
                  <div className="text-[11px] text-gray-500 space-y-1">
                    <p>• Applied on: {selectedApplicant.applied_on}</p>
                    <p>• Last updated: {selectedApplicant.last_updated}</p>
                  </div>
                </div>

                {results && results.status === "success" && (
                  <div className="p-2 rounded bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 text-green-600 dark:text-green-400 text-[11px] flex items-center gap-2">
                    <span className="text-sm">✓</span> Updated successfully
                  </div>
                )}
              </form>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-6 text-gray-400">
                <Bot className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-[13px]">Select an applicant from the list to view or update their status</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

const PolicyCheckView = React.memo(({ onSubmit, isLoading, results }: any) => {
  const [scenario, setScenario] = useState("The candidate is to be offered a 6-month probation along with an on-site relocation allowance of QAR 5,000.");
  const [area, setArea] = useState("Compensation & Benefits");

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar">
        <form id="policy_check_form" onSubmit={(e) => {
          e.preventDefault();
          onSubmit("get_policy_info", { scenario, policy_name: area });
        }} className="space-y-4">
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Scenario / document to check</label>
            <textarea value={scenario} onChange={e => setScenario(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors min-h-[90px] resize-y" />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Policy area</label>
            <select value={area} onChange={e => setArea(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors">
              <option>Compensation & Benefits</option>
              <option>Hiring Process</option>
              <option>Data Privacy (PDPL)</option>
              <option>Equal Opportunity</option>
              <option>All policies</option>
            </select>
          </div>
        </form>

        {results && (
          <div className="mt-4 bg-gray-50 dark:bg-[#252e42] border border-green-500/20 rounded-lg p-3">
            <div className="text-[11px] font-medium text-green-600 dark:text-green-500 mb-1.5">✓ Last check result</div>
            <div className="text-[12px] text-gray-600 dark:text-[#7e8ba0] leading-relaxed">
              {results.message || results.result || "Within compensation bands. Relocation allowance is in approved range. Probation period is policy-compliant."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

const DocAnalyzeView = React.memo(({ onSubmit, isLoading, results }: any) => {
  const [type, setType] = useState("Resume summary & skill extraction");
  const [query, setQuery] = useState("");

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar">
        <form id="doc_analyze_form" onSubmit={(e) => {
          e.preventDefault();
          onSubmit("analyze_file_with_prompt", { type, query });
        }} className="space-y-4">
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Upload document</label>
            <div className="border border-dashed border-gray-300 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-[#2c3650] rounded-lg p-6 text-center cursor-pointer transition-colors">
              <div className="text-[28px] mb-2">📎</div>
              <div className="text-[12px] text-gray-600 dark:text-[#7e8ba0]">Drag or click to upload PDF, DOCX, MP3, or MP4</div>
              <div className="text-[11px] text-gray-500 dark:text-[#4a5568] mt-1">Max 50MB</div>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Analysis type</label>
            <select value={type} onChange={e => setType(e.target.value)} className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors">
              <option>Resume summary & skill extraction</option>
              <option>Interview transcript analysis</option>
              <option>Policy document review</option>
              <option>Custom query</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 dark:text-[#7e8ba0] uppercase tracking-wider mb-1">Custom query (optional)</label>
            <textarea value={query} onChange={e => setQuery(e.target.value)} placeholder="e.g. Summarize the candidate's experience..." className="w-full bg-gray-50 dark:bg-[#252e42] border border-gray-200 dark:border-white/10 rounded-md px-3 py-2 text-[13px] text-gray-900 dark:text-[#dde3f0] focus:outline-none focus:border-blue-500 transition-colors min-h-[70px] resize-y" />
          </div>
        </form>
        {results && <SuccessBox results={results} />}
      </div>
    </div>
  );
});

const SuccessBox = ({ results }: { results: any }) => {
  const findUrl = (obj: any): string | null => {
    if (!obj) return null;
    if (typeof obj === 'string' && (obj.startsWith('http') || obj.endsWith('.pdf') || obj.endsWith('.docx'))) return obj;
    if (typeof obj === 'object') {
      for (const key in obj) {
        if (key.toLowerCase().includes('url') || key.toLowerCase().includes('path') || key.toLowerCase().includes('link')) {
           if (typeof obj[key] === 'string' && obj[key].length > 5) return obj[key];
        }
        const found = findUrl(obj[key]);
        if (found) return found;
      }
    }
    return null;
  };
  
  const downloadUrl = findUrl(results);

  return (
    <div className="mt-4 p-4 rounded-lg bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 text-center">
      <div className="text-[14px] font-semibold text-green-700 dark:text-green-400 mb-1">Task Complete!</div>
      <div className="text-[12px] text-green-600 dark:text-green-500/80">
        {results?.message || results?.result || "The workflow action was completed successfully."}
      </div>
      {downloadUrl && (
        <a 
          href={downloadUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center mt-3 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-[12px] font-medium rounded-md transition-colors"
        >
          Download Document
        </a>
      )}
    </div>
  );
};


// -------------------------------------------------------------
// Main Component
// -------------------------------------------------------------

const TABS = [
  { id: "resume_search", label: "Resume Search", sub: "Match candidates using Vector DB", icon: Search, color: "text-blue-500", bg: "bg-blue-50 dark:bg-[#4f7cff]/15", action: "Search Candidates", formId: "resume_search_form" },
  { id: "job_posting", label: "Job Posting", sub: "Generate JD and publish to platforms", icon: Briefcase, color: "text-teal-500", bg: "bg-teal-50 dark:bg-[#14b8a6]/15", action: "Generate & Publish", formId: "job_posting_form" },
  { id: "schedule_interview", label: "Schedule Interview", sub: "Fix interview slots for candidates", icon: Calendar, color: "text-amber-500", bg: "bg-amber-50 dark:bg-[#f59e0b]/15", action: "Confirm Slot", formId: "schedule_interview_form" },
  { id: "offer_letter", label: "Offer Letter", sub: "Generate offer letters from templates", icon: FileSignature, color: "text-green-500", bg: "bg-green-50 dark:bg-[#22c55e]/15", action: "Generate Letter", formId: "offer_letter_form" },
  { id: "send_email", label: "Send Email", sub: "Send professional emails to stakeholders", icon: Mail, color: "text-red-500", bg: "bg-red-50 dark:bg-[#ef4444]/15", action: "Send Email", formId: "send_email_form" },
  { id: "app_status", label: "App Status", sub: "Fetch or update application status from ATS", icon: Activity, color: "text-purple-500", bg: "bg-purple-50 dark:bg-[#a78bfa]/15", action: "Fetch / Update", formId: "app_status_form" },
  { id: "policy", label: "Policy Check", sub: "Verify compliance for documents or decisions", icon: ShieldCheck, color: "text-pink-500", bg: "bg-pink-50 dark:bg-[#f472b6]/15", action: "Check Compliance", formId: "policy_check_form" },
  { id: "doc_analyze", label: "Doc Analyze", sub: "Extract insights from uploaded documents", icon: FileText, color: "text-yellow-600 dark:text-yellow-500", bg: "bg-yellow-50 dark:bg-[rgba(251,191,36,.12)]", action: "Analyze Document", formId: "doc_analyze_form" },
];

const ManualWorkflowApp: React.FC<ManualWorkflowAppProps> = ({ isOpen, onClose, onInvokeTool }) => {
  const [windowState, setWindowState] = useState<'normal' | 'maximized' | 'minimized'>('normal');
  const [activeTab, setActiveTab] = useState(TABS[0].id);

  // Submission State
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<any>(null);

  // Reset state when tab changes
  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId);
    setResults(null);
    setIsLoading(false);
  }, []);

  const handleSubmit = useCallback(async (toolName: string, params: any) => {
    console.log("[ManualWorkflow] handleSubmit called:", toolName, params);
    setIsLoading(true);
    setResults(null);
    try {
      const result = await onInvokeTool(toolName, params);
      console.log("[ManualWorkflow] Result received:", result);
      setResults(result);
    } catch (err: any) {
      console.error("[ManualWorkflow] Error:", err);
      const errorMsg = err?.message?.includes("No active session")
        ? "⚠️ No active Recruitment Agent session found. Please start a chat with the Recruitment Agent first, then open Manual Workflow."
        : `❌ Failed to fetch results: ${err?.message || "Unknown error"}`;
      setResults({ error: errorMsg });
    } finally {
      setIsLoading(false);
    }
  }, [onInvokeTool]);

  const activeTabData = useMemo(() => TABS.find(t => t.id === activeTab), [activeTab]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      {windowState !== 'minimized' && (
        <div 
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm transition-opacity duration-300 pointer-events-auto"
          onClick={onClose}
        />
      )}

      {/* Panel / Window */}
      <div 
        className={`fixed z-[101] flex flex-col bg-white dark:bg-[#161c27] border border-gray-200 dark:border-white/10 shadow-2xl overflow-hidden pointer-events-auto transition-all duration-300 ease-[cubic-bezier(0.34,1.25,0.64,1)] ${
          windowState === 'maximized' 
            ? "inset-0 w-full h-full rounded-none" 
            : windowState === 'minimized'
            ? "bottom-0 right-[24px] md:right-[40px] w-[320px] h-[44px] rounded-t-[12px] rounded-b-none"
            : "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[760px] max-w-[93vw] h-[560px] max-h-[88vh] min-w-[480px] min-h-[400px] rounded-[16px]"
        }`}
        style={{
          boxShadow: windowState !== 'minimized' ? '0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)' : '0 -10px 40px rgba(0,0,0,0.3)'
        }}
      >
        {/* MacOS-style Titlebar */}
        <div 
          className={`h-[44px] shrink-0 bg-gray-50 dark:bg-[#1d2535] border-b border-gray-200 dark:border-white/10 flex items-center px-[14px] gap-[10px] select-none ${windowState === 'minimized' ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-[#252e42] transition-colors' : ''}`}
          onClick={() => windowState === 'minimized' && setWindowState('normal')}
        >
          <div className="flex gap-[7px] items-center">
            <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="w-[13px] h-[13px] rounded-full bg-[#ff5f57] hover:brightness-110 transition-all focus:outline-none" />
            <button onClick={(e) => { e.stopPropagation(); setWindowState(windowState === 'minimized' ? 'normal' : 'minimized'); }} className="w-[13px] h-[13px] rounded-full bg-[#febc2e] hover:brightness-110 transition-all focus:outline-none" />
            <button onClick={(e) => { e.stopPropagation(); setWindowState(windowState === 'maximized' ? 'normal' : 'maximized'); }} className="w-[13px] h-[13px] rounded-full bg-[#28c840] hover:brightness-110 transition-all focus:outline-none" />
          </div>
          <div className="flex-1 text-center text-[12px] font-medium text-gray-500 dark:text-[#4a5568] tracking-wide">
            Recruitment Management — Manual Workflow
          </div>
          {windowState !== 'minimized' && (
            <button 
              onClick={(e) => { e.stopPropagation(); setWindowState(windowState === 'maximized' ? 'normal' : 'maximized'); }}
              className="w-[26px] h-[26px] rounded-md border border-transparent hover:border-gray-200 dark:hover:border-white/10 hover:bg-gray-100 dark:hover:bg-[#252e42] text-gray-500 dark:text-[#4a5568] hover:text-gray-900 dark:hover:text-[#dde3f0] flex items-center justify-center transition-colors focus:outline-none"
            >
              {windowState === 'maximized' ? <Minimize2 className="w-[13px] h-[13px]" /> : <Maximize2 className="w-[13px] h-[13px]" />}
            </button>
          )}
        </div>

        {/* Two-pane Body */}
        <div className={`flex flex-1 overflow-hidden min-h-0 bg-white dark:bg-[#161c27] ${windowState === 'minimized' ? 'hidden' : ''}`}>
          {/* LEFT sidebar */}
          <div className="w-[196px] shrink-0 border-r border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#1d2535] p-3 overflow-y-auto custom-scrollbar">
            <div className="text-[9px] text-gray-500 dark:text-[#4a5568] uppercase tracking-[0.12em] font-medium px-2 mb-2">
              All Tasks
            </div>
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-[10px] w-full px-[10px] py-[8px] rounded-[8px] text-left transition-colors mb-[2px] focus:outline-none ${
                    isActive 
                      ? "bg-blue-50 dark:bg-[rgba(79,124,255,0.15)] outline outline-1 outline-[rgba(79,124,255,0.3)]" 
                      : "hover:bg-gray-100 dark:hover:bg-[#252e42]"
                  }`}
                >
                  <div className={`w-[30px] h-[30px] rounded-[7px] flex items-center justify-center shrink-0 ${tab.bg}`}>
                    <Icon className={`w-4 h-4 ${tab.color}`} />
                  </div>
                  <div>
                    <div className="text-[12px] font-medium text-gray-900 dark:text-[#dde3f0] leading-[1.3]">{tab.label}</div>
                    <div className="text-[10px] text-gray-500 dark:text-[#4a5568] leading-[1.3] mt-[1px] truncate w-[120px]">{tab.sub}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* RIGHT form pane */}
          <div className="flex-1 flex flex-col min-w-0">
            {activeTabData && (
              <>
                <div className="px-[18px] pt-[16px] pb-[12px] shrink-0 flex items-center gap-[11px] border-b border-gray-200 dark:border-white/10">
                  <div className={`w-[36px] h-[36px] rounded-[9px] flex items-center justify-center shrink-0 ${activeTabData.bg}`}>
                    <activeTabData.icon className={`w-[18px] h-[18px] ${activeTabData.color}`} />
                  </div>
                  <div>
                    <div className="text-[15px] font-semibold text-gray-900 dark:text-[#dde3f0] leading-[1.2]">{activeTabData.label}</div>
                    <div className="text-[11px] text-gray-500 dark:text-[#4a5568] mt-[2px]">{activeTabData.sub}</div>
                  </div>
                </div>

                <div className="flex-1 overflow-hidden">
                  {activeTab === "resume_search" && <ResumeSearchView onSubmit={handleSubmit} isLoading={isLoading} results={results} />}
                  {activeTab === "job_posting" && <JobPostingView onSubmit={handleSubmit} isLoading={isLoading} results={results} />}
                  {activeTab === "schedule_interview" && <ScheduleInterviewView onSubmit={handleSubmit} isLoading={isLoading} results={results} />}
                  {activeTab === "offer_letter" && <OfferLetterView onSubmit={handleSubmit} isLoading={isLoading} results={results} />}
                  {activeTab === "send_email" && <SendEmailView onSubmit={handleSubmit} isLoading={isLoading} results={results} />}
                  {activeTab === "app_status" && <AppStatusView onSubmit={handleSubmit} isLoading={isLoading} results={results} onInvokeTool={onInvokeTool} />}
                  {activeTab === "policy" && <PolicyCheckView onSubmit={handleSubmit} isLoading={isLoading} results={results} />}
                  {activeTab === "doc_analyze" && <DocAnalyzeView onSubmit={handleSubmit} isLoading={isLoading} results={results} />}
                </div>

                <div className="px-[18px] py-[11px] border-t border-gray-200 dark:border-white/10 flex gap-[8px] justify-end shrink-0 bg-gray-50 dark:bg-[#1d2535]">
                  <button 
                    onClick={onClose}
                    className="px-[16px] py-[7px] rounded-[7px] text-[13px] font-medium border border-gray-200 dark:border-white/11 bg-transparent text-gray-600 dark:text-[#7e8ba0] hover:bg-gray-100 dark:hover:bg-[#252e42] hover:text-gray-900 dark:hover:text-[#dde3f0] transition-colors focus:outline-none"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    form={activeTabData.formId}
                    disabled={isLoading}
                    className="px-[16px] py-[7px] rounded-[7px] text-[13px] font-medium border border-blue-600 dark:border-[#4f7cff] bg-blue-600 dark:bg-[#4f7cff] text-white hover:bg-blue-700 dark:hover:bg-[#3d63d4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none flex items-center gap-2"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {activeTabData.action} &rarr;
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ManualWorkflowApp;